import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Search, ChevronDown, ChevronUp } from 'lucide-react';
import { formatDistance } from 'date-fns';
import axiosInstance from '../../../shared/services/api/axiosInstance';
import UserDetailExpanded from './UserDetailExpanded';

/**
 * UserJourneyTable Component
 * Displays users table with search and filters
 * Expands user details inline on row click
 * Fetches paginated users from /api/user-identities endpoint:
 * - Initially on mount (page 1)
 * - On demand when pagination changes
 * - On demand when search term changes (debounced)
 * - On demand when filters change
 */
const UserJourneyTable = ({ onRowClick, expandedUserId, teamId, websiteId, appliedFilters = [], dateRange = null, initialSearchTerm = '', userIdFromUrl = null }) => {
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm || '');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [sortConfig, setSortConfig] = useState({
    column: 'last_session',
    direction: 'desc'
  });
  const abortControllerRef = useRef(null);
  // Cache for user lists: key = `${teamId}_${websiteId}_${page}_${searchTerm}_${sortConfig}`
  const usersCacheRef = useRef(new Map());
  
  // Auto-expand user when userIdFromUrl matches (similar to SessionsTable)
  useEffect(() => {
    if (userIdFromUrl && users.length > 0 && !isLoading && onRowClick) {
      const matchingUser = users.find(u => 
        u.userId === userIdFromUrl || 
        u.userId?.toLowerCase() === userIdFromUrl.toLowerCase()
      );
      if (matchingUser && expandedUserId !== matchingUser.userId) {
        // Auto-expand the matching user
        onRowClick(matchingUser);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIdFromUrl, users, isLoading, expandedUserId]);
  
  const styles = {
    primaryBg: "#ffffff",
    secondaryBg: "#DDE2EE",
    textPrimary: "#0f172a",
    textSecondary: "#666666",
    accentcolor: "#0f172a",
    borderColor: "#DDE2EE"
  };

  // Fetch users from API ON DEMAND with caching:
  // - Initially when component mounts (if teamId/websiteId are available)
  // - When pagination changes (currentPage)
  // - When search term changes (debounced)
  // - When filters change
  // - Uses cache to avoid refetching already loaded data
  useEffect(() => {
    if (!teamId || !websiteId) {
      setUsers([]);
      setTotal(0);
      return;
    }

    // Create cache key including filters and sort
    const searchKey = searchTerm.trim() || '';
    const filtersKey = JSON.stringify(appliedFilters);
    const sortKey = `${sortConfig.column}_${sortConfig.direction}`;
    const cacheKey = `${teamId}_${websiteId}_${currentPage}_${searchKey}_${filtersKey}_${sortKey}`;

    // Check cache first (but skip cache if filters are applied to ensure fresh data)
    if (appliedFilters.length === 0) {
      const cachedData = usersCacheRef.current.get(cacheKey);
      if (cachedData) {
        setUsers(cachedData.users);
        setTotal(cachedData.total);
        setIsLoading(false);
        return;
      }
    }

    // Abort previous request if still pending
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const fetchUsers = async () => {
      setIsLoading(true);
      setError(null);

      // Create new abort controller for this request
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const params = {
          team_id: teamId,
          website_id: websiteId,
          page: currentPage,
          limit: pageSize
        };

        if (searchTerm.trim()) {
          params.search = searchTerm.trim();
        }

        // Add date range if available
        if (dateRange) {
          const dateRangeObj = typeof dateRange === 'object' && dateRange.from ? dateRange : null;
          if (dateRangeObj) {
            if (dateRangeObj.from) params.from = dateRangeObj.from;
            if (dateRangeObj.to) params.to = dateRangeObj.to;
          }
        }

        // Add modular filters if available
        if (appliedFilters && appliedFilters.length > 0) {
          params.modularFilters = JSON.stringify(appliedFilters);
        }

        // Add sorting parameters
        params.sort_by = sortConfig.column;
        params.sort_order = sortConfig.direction;

        const response = await axiosInstance.get('/user-identities', {
          params,
          signal: abortController.signal
        });

        // Check if request was aborted
        if (abortController.signal.aborted) {
          return;
        }

        if (response.data && response.data.success) {
          const fetchedUsers = response.data.users || [];
          const fetchedTotal = response.data.total || 0;
          
          // Store in cache only if no filters applied
          if (appliedFilters.length === 0) {
            usersCacheRef.current.set(cacheKey, {
              users: fetchedUsers,
              total: fetchedTotal
            });
          }
          
          setUsers(fetchedUsers);
          setTotal(fetchedTotal);
        } else {
          setError('Failed to load users');
          setUsers([]);
          setTotal(0);
        }
      } catch (err) {
        // Don't set error if request was aborted
        if (err.name === 'AbortError' || err.name === 'CanceledError') {
          return;
        }
        console.error('Error fetching users:', err);
        setError(err.response?.data?.message || err.message || 'Failed to load users');
        setUsers([]);
        setTotal(0);
      } finally {
        if (!abortController.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Debounce search - wait 300ms after user stops typing
    // No debounce for pagination or filter changes (immediate fetch)
    const timeoutId = setTimeout(() => {
      fetchUsers();
    }, searchTerm.trim() ? 300 : 0);

    // Cleanup: abort request and clear timeout
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [teamId, websiteId, currentPage, pageSize, searchTerm, appliedFilters, dateRange, sortConfig]);

  // Reset to page 1 when search term, filters, or sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, appliedFilters, sortConfig]);

  const formatNetworth = (networth) => {
    if (networth === null || networth === undefined) return 'N/A';
    return `$${networth.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 })}`;
  };

  const shortenUserId = (userId) => {
    if (!userId) return '';
    return userId.length > 12 ? `${userId.substring(0, 12)}...` : userId;
  };

  const formatLastSession = (lastVisit) => {
    if (!lastVisit) return 'N/A';
    try {
      const date = new Date(lastVisit);
      return formatDistance(date, new Date(), { addSuffix: true });
    } catch (e) {
      return 'N/A';
    }
  };

  // Handle column sorting
  const handleSort = (column) => {
    setSortConfig(prevConfig => {
      if (prevConfig.column === column) {
        // Same column clicked - toggle between asc and desc
        return {
          column,
          direction: prevConfig.direction === 'desc' ? 'asc' : 'desc'
        };
      } else {
        // New column - start with desc (as per requirements)
        return {
          column,
          direction: 'desc'
        };
      }
    });
  };

  // Render sort indicator for a column
  const renderSortIndicator = (columnKey) => {
    if (sortConfig.column !== columnKey) {
      return null; // No indicator for inactive columns
    }
    return sortConfig.direction === 'desc' ? (
      <ChevronDown className="h-4 w-4 inline-block ml-1" style={{ color: styles.textPrimary }} />
    ) : (
      <ChevronUp className="h-4 w-4 inline-block ml-1" style={{ color: styles.textPrimary }} />
    );
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-4">
      {/* Users Table */}
      <div className="overflow-hidden" style={{ backgroundColor: styles.primaryBg, border: `1px solid ${styles.borderColor}` }}>
        <div className="px-6 py-4 flex items-center justify-between gap-4" style={{ borderBottom: `1px solid ${styles.borderColor}` }}>
          <h3 className="text-sm font-semibold" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
            Users {total > 0 && `(${total})`}
          </h3>
          {/* Search Bar beside Users heading */}
          <div className="flex-1 max-w-md relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5" style={{ color: styles.textSecondary }} />
            <input
              type="text"
              placeholder="Search by user ID, name, email, phone, telegram, discord, x, farcaster, instagram, city, or country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:border-transparent"
              style={{ 
                fontFamily: 'Raleway, sans-serif',
                border: `1px solid ${styles.borderColor}`,
                backgroundColor: styles.primaryBg,
                color: styles.textPrimary
              }}
              onFocus={(e) => e.target.style.boxShadow = `0 0 0 2px ${styles.accentColor}`}
              onBlur={(e) => e.target.style.boxShadow = 'none'}
            />
          </div>
        </div>

        {/* Table Header */}
        <div className="sticky top-0 z-10 px-6 py-3" style={{ backgroundColor: styles.primaryBg, borderBottom: `1px solid ${styles.borderColor}` }}>
          <div className="grid grid-cols-5 gap-4 text-sm font-medium" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
            <div>USER ID</div>
            <div>GEOGRAPHY</div>
            <div 
              className="flex items-center cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => handleSort('last_session')}
            >
              LAST SESSION
              {renderSortIndicator('last_session')}
            </div>
            <div 
              className="flex items-center cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => handleSort('sessions')}
            >
              SESSIONS
              {renderSortIndicator('sessions')}
            </div>
            <div 
              className="flex items-center cursor-pointer hover:opacity-70 transition-opacity"
              onClick={() => handleSort('networth')}
            >
              NETWORTH
              {renderSortIndicator('networth')}
            </div>
          </div>
        </div>

        {/* Table Body */}
        <div style={{ borderTop: `1px solid ${styles.borderColor}` }}>
          {isLoading ? (
            <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              Loading users...
            </div>
          ) : error ? (
            <div className="px-6 py-8 text-center" style={{ color: '#ef4444', fontFamily: 'Raleway, sans-serif' }}>
              Error: {error}
            </div>
          ) : users.length === 0 ? (
            <div className="px-6 py-8 text-center" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
              {searchTerm.trim() ? 'No users found matching your search.' : 'No users found.'}
            </div>
          ) : (
            <>
              {users.map((user, index) => {
                const isExpanded = expandedUserId === user.userId;
                return (
                  <React.Fragment key={user.userId || index}>
                    <div
                      onClick={() => onRowClick && onRowClick(user)}
                      className="grid grid-cols-5 gap-4 px-6 py-4 cursor-pointer transition-colors"
                      style={{ 
                        borderBottom: isExpanded ? 'none' : (index < users.length - 1 ? `1px solid ${styles.borderColor}` : 'none'),
                        backgroundColor: isExpanded ? styles.secondaryBg : 'transparent'
                      }}
                      onMouseEnter={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = styles.secondaryBg)}
                      onMouseLeave={(e) => !isExpanded && (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      <div className="text-sm font-medium" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {shortenUserId(user.userId)}
                      </div>
                      <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {user.geography}
                      </div>
                      <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {formatLastSession(user.lastSession)}
                      </div>
                      <div className="text-sm" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        {user.sessions}
                      </div>
                      <div className="text-sm flex items-center justify-between" style={{ color: styles.textPrimary, fontFamily: 'Raleway, sans-serif' }}>
                        <span>{formatNetworth(user.networthUsd)}</span>
                        {isExpanded ? (
                          <ChevronUp className="h-4 w-4" style={{ color: styles.textPrimary }} />
                        ) : (
                          <ChevronDown className="h-4 w-4" style={{ color: styles.textSecondary }} />
                        )}
                      </div>
                    </div>
                    {isExpanded && (
                      <div style={{ borderBottom: index < users.length - 1 ? `1px solid ${styles.borderColor}` : 'none' }}>
                        <UserDetailExpanded 
                          user={user} 
                          teamId={teamId}
                          websiteId={websiteId}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-6 py-4 flex items-center justify-between" style={{ borderTop: `1px solid ${styles.borderColor}` }}>
                  <div className="text-sm" style={{ color: styles.textSecondary, fontFamily: 'Raleway, sans-serif' }}>
                    Page {currentPage} of {totalPages}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1 || isLoading}
                      className="px-3 py-1 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        border: `1px solid ${styles.borderColor}`,
                        backgroundColor: styles.primaryBg,
                        color: styles.textPrimary
                      }}
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages || isLoading}
                      className="px-3 py-1 text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ 
                        fontFamily: 'Raleway, sans-serif',
                        border: `1px solid ${styles.borderColor}`,
                        backgroundColor: styles.primaryBg,
                        color: styles.textPrimary
                      }}
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserJourneyTable;

